import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Assessment,
  Block,
  CheckCircle,
  Close,
  FileDownloadOutlined,
  Recommend,
  RemoveRedEye,
  Schedule,
  Search,
  Speed,
  ThumbUp,
  Warning,
} from '@mui/icons-material';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';
import { vsaAPI } from '../../services/api';
import { colors, status } from '../../theme';
import {
  EmptyState,
  fmt,
  getDisplayText,
  getDisplayTone,
  scoreColor,
  tdSx,
  thSx,
} from './shared';

const DISPLAY_ICONS = {
  thumb_up: ThumbUp,
  remove_red_eye: RemoveRedEye,
  block: Block,
  schedule: Schedule,
};

export default function TickerDetailDrawer({ open, onClose, ticker }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    if (!open || !ticker) return;
    setLoading(true);
    vsaAPI.getTickerDetail(ticker, 30)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, ticker]);

  const handleExport = useCallback(async () => {
    if (!captureRef.current || exporting) return;
    setExporting(true);
    const node = captureRef.current;
    const scrollEl = node.querySelector('[data-capture-scroll]');
    const prevNode = {
      height: node.style.height,
      maxHeight: node.style.maxHeight,
    };
    const prev = scrollEl ? {
      overflow: scrollEl.style.overflow,
      height: scrollEl.style.height,
      maxHeight: scrollEl.style.maxHeight,
    } : null;
    if (scrollEl) {
      scrollEl.style.overflow = 'visible';
      scrollEl.style.height = 'auto';
      scrollEl.style.maxHeight = 'none';
    }
    node.style.height = 'auto';
    node.style.maxHeight = 'none';
    const ignored = Array.from(node.querySelectorAll('[data-html2canvas-ignore="true"]'));
    const ignoredPrev = ignored.map((el) => ({ el, display: el.style.display }));
    ignored.forEach((el) => { el.style.display = 'none'; });
    const fullwidth = Array.from(node.querySelectorAll('[data-export-fullwidth="true"]'));
    const fullwidthPrev = fullwidth.map((el) => ({
      el,
      maxWidth: el.style.maxWidth,
      overflow: el.style.overflow,
      textOverflow: el.style.textOverflow,
      whiteSpace: el.style.whiteSpace,
    }));
    fullwidth.forEach((el) => {
      el.style.maxWidth = 'none';
      el.style.overflow = 'visible';
      el.style.textOverflow = 'clip';
      el.style.whiteSpace = 'normal';
    });
    await new Promise((resolve) => setTimeout(resolve, 80));
    try {
      const rect = node.getBoundingClientRect();
      const captureHeight = Math.ceil(rect.height);
      const canvas = await html2canvas(node, {
        backgroundColor: colors.gray[50],
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
        windowHeight: captureHeight,
        height: captureHeight,
        width: node.scrollWidth,
      });
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `VSA_${ticker}_${dateStr}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      ignoredPrev.forEach(({ el, display }) => { el.style.display = display; });
      fullwidthPrev.forEach(({ el, maxWidth, overflow, textOverflow, whiteSpace }) => {
        el.style.maxWidth = maxWidth;
        el.style.overflow = overflow;
        el.style.textOverflow = textOverflow;
        el.style.whiteSpace = whiteSpace;
      });
      if (scrollEl && prev) {
        scrollEl.style.overflow = prev.overflow;
        scrollEl.style.height = prev.height;
        scrollEl.style.maxHeight = prev.maxHeight;
      }
      node.style.height = prevNode.height;
      node.style.maxHeight = prevNode.maxHeight;
      setExporting(false);
    }
  }, [ticker, exporting]);

  const latest = data?.latest;
  const analytics = data?.analytics || {};
  const history = data?.history || [];
  const companyName = data?.company_name;
  const sectorNameObj = data?.sector_name;
  const sectorLabel = sectorNameObj ? (sectorNameObj[lang] || sectorNameObj.en || sectorNameObj.vi) : null;
  const tradePlan = latest?.display?.trade_plan || {};
  const verdict = latest?.display?.verdict || {};
  const verdictTone = getDisplayTone(verdict.tone);
  const VerdictIcon = DISPLAY_ICONS[verdict.icon] || RemoveRedEye;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 }, bgcolor: colors.gray[50] } }}>
      <Box ref={captureRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: colors.background.paper, borderBottom: `1px solid ${colors.gray[200]}` }}>
          {latest ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: 0.75, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, bgcolor: colors.gray[800], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 900, color: colors.background.paper, fontSize: '0.88rem', lineHeight: 1, letterSpacing: '0.02em' }}>{ticker}</Typography>
                </Box>
                <Box sx={{ height: 12, bgcolor: status.purple.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 800, color: colors.background.paper, fontSize: '0.52rem', letterSpacing: '0.12em', lineHeight: 1 }}>VSA</Typography>
                </Box>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                  <Typography data-export-fullwidth="true" sx={{ fontWeight: 800, fontSize: '1rem', color: colors.gray[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{companyName || ticker}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.3, flexWrap: 'wrap' }}>
                  {sectorLabel && (
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: colors.info.dark, bgcolor: alpha(colors.info.dark, 0.08), px: 0.7, py: 0.1, borderRadius: 0.8 }}>
                      {sectorLabel}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.66rem', color: colors.gray[500] }}>{getDisplayText(latest.display?.setup, t, latest.setup_type)}</Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontWeight: 900, fontSize: '1.15rem' }}>{ticker}</Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-html2canvas-ignore="true">
            {latest && (
              <Tooltip title={t('vsa.detail.export')} placement="bottom">
                <span>
                  <IconButton onClick={handleExport} size="small" disabled={exporting} sx={{ color: colors.gray[600] }}>
                    {exporting ? <CircularProgress size={18} /> : <FileDownloadOutlined sx={{ fontSize: 20 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
                  <IconButton onClick={onClose} size="small" sx={{ color: colors.gray[600] }}><Close sx={{ fontSize: 20 }} /></IconButton>
          </Box>
        </Box>

        <Box data-capture-scroll sx={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : !latest ? (
            <EmptyState Icon={Search} title={t('vsa.detail.noData')} />
          ) : (
            <Box>
              <Box sx={{ m: 2, p: 1.6, borderRadius: 1.5, bgcolor: verdictTone.bg, border: `1px solid ${verdictTone.border}`, display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.2, bgcolor: verdictTone.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 3px 10px ${alpha(verdictTone.color, 0.35)}` }}>
                  <VerdictIcon sx={{ fontSize: 20, color: colors.background.paper }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: verdictTone.color, lineHeight: 1.2, letterSpacing: '0.02em' }}>{getDisplayText({ key: verdict.label_key }, t, latest.suggested_action)}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: colors.gray[600], mt: 0.4, lineHeight: 1.4 }}>{getDisplayText({ key: verdict.desc_key }, t, '')}</Typography>
                </Box>
              </Box>

              <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                  <Recommend sx={{ fontSize: 14, color: colors.gray[500] }} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600], textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('vsa.detail.tradePlan')}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: colors.gray[400], ml: 'auto', fontFamily: 'monospace' }}>{latest.date}</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', bgcolor: colors.gray[200], borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${colors.gray[200]}` }}>
                  {[
                    { label: t('vsa.detail.price'), value: fmt(latest.close), sub: null, color: colors.gray[900] },
                    { label: t('vsa.detail.entryZone'), value: latest.entry_zone_low && latest.entry_zone_high ? `${fmt(latest.entry_zone_low)}–${fmt(latest.entry_zone_high)}` : fmt(latest.entry_mid), sub: null, color: colors.info.main },
                    { label: t('vsa.detail.stop'), value: fmt(latest.stop_loss), sub: tradePlan.risk_pct != null ? `−${tradePlan.risk_pct.toFixed(1)}%` : null, color: colors.error.main },
                    { label: t('vsa.detail.target1'), value: fmt(latest.target_1), sub: latest.reward_1_pct != null ? `+${(latest.reward_1_pct * 100).toFixed(1)}%` : null, color: colors.success.main },
                    { label: t('vsa.detail.target2'), value: fmt(latest.target_2), sub: latest.reward_2_pct != null ? `+${(latest.reward_2_pct * 100).toFixed(1)}%` : null, color: colors.success.dark },
                    { label: t('vsa.detail.rrRatio'), value: tradePlan.rr_ratio != null ? `${tradePlan.rr_ratio.toFixed(2)}` : '—', sub: tradePlan.risk_pct != null && latest.reward_1_pct != null ? `${tradePlan.risk_pct.toFixed(1)}% / ${(latest.reward_1_pct * 100).toFixed(1)}%` : null, color: getDisplayTone(tradePlan.rr_tone).color },
                  ].map((metric, index) => (
                    <Box key={index} sx={{ bgcolor: colors.background.paper, px: 1.4, py: 1 }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.gray[400] }}>{metric.label}</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 900, fontFamily: 'monospace', color: metric.color, lineHeight: 1.2 }}>{metric.value}</Typography>
                      {metric.sub && <Typography sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: colors.gray[400], mt: 0.2 }}>{metric.sub}</Typography>}
                    </Box>
                  ))}
                </Box>
              </Box>

              {(() => {
                const items = latest.display?.context_items || [];
                if (items.length === 0) return null;
                return (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                      <Speed sx={{ fontSize: 14, color: colors.gray[500] }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600], textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('vsa.detail.contextTitle')}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', bgcolor: colors.gray[200], borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${colors.gray[200]}` }}>
                      {items.map((item, index) => {
                        const tone = getDisplayTone(item.tone);
                        return (
                        <Box key={index} sx={{ bgcolor: colors.background.paper, px: 1.4, py: 0.9 }}>
                          <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.gray[400] }}>{t(item.label_key)}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: tone.color, lineHeight: 1.2 }}>{getDisplayText(item, t, '')}</Typography>
                        </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}

              {(() => {
                const reasons = latest.display?.reasons || [];
                const cautions = latest.display?.cautions || [];
                const timing = latest.display?.timing || null;

                return (
                  <>
                    {reasons.length > 0 && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                          <Assessment sx={{ fontSize: 14, color: colors.gray[500] }} />
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600], textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('vsa.detail.reasonsTitle')}</Typography>
                        </Box>
                        <Box sx={{ bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, borderRadius: 1.5, overflow: 'hidden' }}>
                          {reasons.map((reason, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, px: 1.4, py: 0.9, borderBottom: index < reasons.length - 1 ? `1px solid ${colors.gray[100]}` : 'none' }}>
                              <CheckCircle sx={{ fontSize: 14, color: getDisplayTone(reason.tone).color, mt: '2px', flexShrink: 0 }} />
                              <Typography sx={{ fontSize: '0.74rem', color: colors.gray[800], lineHeight: 1.45 }}>{t(reason.key, reason.params || {})}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {cautions.length > 0 && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                          <Warning sx={{ fontSize: 14, color: colors.warning.main }} />
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.warning.dark, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('vsa.detail.cautionsTitle')}</Typography>
                        </Box>
                        <Box sx={{ bgcolor: alpha(colors.warning.main, 0.05), border: `1px solid ${alpha(colors.warning.main, 0.25)}`, borderRadius: 1.5, overflow: 'hidden' }}>
                          {cautions.map((caution, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, px: 1.4, py: 0.9, borderBottom: index < cautions.length - 1 ? `1px solid ${alpha(colors.warning.main, 0.15)}` : 'none' }}>
                              <Warning sx={{ fontSize: 14, color: colors.warning.main, mt: '2px', flexShrink: 0 }} />
                              <Typography sx={{ fontSize: '0.74rem', color: status.warning.text, lineHeight: 1.45 }}>{t(caution.key, caution.params || {})}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {timing ? (
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                        <Schedule sx={{ fontSize: 14, color: colors.gray[500] }} />
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600], textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('vsa.detail.timingTitle')}</Typography>
                      </Box>
                      <Box sx={{ bgcolor: alpha(colors.info.main, 0.04), border: `1px solid ${alpha(colors.info.main, 0.2)}`, borderRadius: 1.5, px: 1.4, py: 1, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Schedule sx={{ fontSize: 16, color: colors.info.main, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.78rem', color: colors.gray[800], fontWeight: 600, lineHeight: 1.4 }}>{getDisplayText(timing, t, '')}</Typography>
                      </Box>
                    </Box>
                    ) : null}
                  </>
                );
              })()}

              {history.length > 0 && (
                <Box>
                  <Box sx={{ px: 2, py: 0.8, borderBottom: `1px solid ${colors.gray[200]}`, borderTop: `1px solid ${colors.gray[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>{t('vsa.detail.signalHistory')}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: colors.gray[400] }}>{t('vsa.detail.records', { count: history.length })}</Typography>
                  </Box>
                  {(() => {
                    const chartData = [...history].reverse().map((item) => ({
                      date: item.date,
                      dateShort: item.date?.slice(5),
                      close: item.close,
                      volume: item.volume,
                      grade: item.execution_grade,
                      setup: item.setup_type,
                      action: item.suggested_action,
                      score: item.priority_score,
                      display: item.display,
                      markerY: ['A', 'B', 'C'].includes(item.execution_grade) && ['BUY_PROBE_OR_ADD', 'BUY_PROBE', 'WATCHLIST_READY'].includes(item.suggested_action) ? item.close : null,
                    }));
                    const closes = chartData.map((item) => item.close).filter((value) => value != null);
                    const minClose = closes.length ? Math.min(...closes) : 0;
                    const maxClose = closes.length ? Math.max(...closes) : 1;
                    const padding = (maxClose - minClose) * 0.08 || 1;
                    const yDomain = [Math.max(0, minClose - padding), maxClose + padding];
                    const fmtVol = (value) => {
                      if (value == null) return '';
                      if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
                      if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
                      if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
                      return String(value);
                    };
                    const ChartTooltip = ({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const datum = payload[0]?.payload;
                      if (!datum) return null;
                      const gradeTone = getDisplayTone(datum.display?.grade?.tone).color;
                      return (
                        <Box sx={{ bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, borderRadius: 1, px: 1.2, py: 0.8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.gray[800], fontFamily: 'monospace' }}>{datum.date}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: colors.gray[600], mt: 0.3 }}>{t('vsa.detail.headers.close')}: <b style={{ fontFamily: 'monospace' }}>{fmt(datum.close)}</b></Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: colors.gray[600] }}>{t('vsa.detail.volume')}: <b style={{ fontFamily: 'monospace' }}>{fmtVol(datum.volume)}</b></Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: gradeTone, bgcolor: alpha(gradeTone, 0.12), px: 0.6, py: 0.05, borderRadius: 0.6 }}>{datum.display?.grade?.text || datum.grade}</Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: colors.gray[500] }}>{getDisplayText(datum.display?.setup, t, datum.setup)} · {getDisplayText(datum.display?.action, t, datum.action || 'WATCH')}</Typography>
                          </Box>
                          {datum.score != null && <Typography sx={{ fontSize: '0.62rem', color: colors.gray[400], mt: 0.2, fontFamily: 'monospace' }}>{t('vsa.detail.headers.score')}: {Math.round(datum.score)}</Typography>}
                        </Box>
                      );
                    };
                    return (
                      <Box sx={{ px: 1, pt: 1.5, pb: 0.5, bgcolor: colors.background.paper }}>
                        <Box sx={{ height: 180 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={colors.gray[100]} vertical={false} />
                              <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: colors.gray[400] }} axisLine={{ stroke: colors.gray[200] }} tickLine={false} />
                              <YAxis domain={yDomain} tick={{ fontSize: 10, fill: colors.gray[400] }} axisLine={false} tickLine={false} width={50} tickFormatter={(value) => fmt(value)} />
                              <RTooltip content={<ChartTooltip />} />
                              {latest?.entry_mid && <ReferenceLine y={latest.entry_mid} stroke={colors.info.main} strokeDasharray="3 3" strokeWidth={1} label={{ value: 'Entry', position: 'right', fill: colors.info.main, fontSize: 9 }} />}
                              {latest?.stop_loss && <ReferenceLine y={latest.stop_loss} stroke={colors.error.main} strokeDasharray="3 3" strokeWidth={1} label={{ value: 'Stop', position: 'right', fill: colors.error.main, fontSize: 9 }} />}
                              {latest?.target_1 && <ReferenceLine y={latest.target_1} stroke={colors.success.main} strokeDasharray="3 3" strokeWidth={1} label={{ value: 'T1', position: 'right', fill: colors.success.main, fontSize: 9 }} />}
                              <Line type="monotone" dataKey="close" stroke={colors.gray[800]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                              <Scatter dataKey="markerY" isAnimationActive={false} shape="circle">
                                {chartData.map((item, index) => <Cell key={index} fill={item.markerY != null ? getDisplayTone(item.display?.grade?.tone).color : 'transparent'} stroke={colors.background.paper} strokeWidth={1.5} />)}
                              </Scatter>
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>
                        <Box sx={{ height: 70, mt: 0.5 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={colors.gray[100]} vertical={false} />
                              <XAxis dataKey="dateShort" tick={{ fontSize: 9, fill: colors.gray[400] }} axisLine={{ stroke: colors.gray[200] }} tickLine={false} />
                              <YAxis tick={{ fontSize: 9, fill: colors.gray[400] }} axisLine={false} tickLine={false} width={50} tickFormatter={fmtVol} />
                              <RTooltip content={<ChartTooltip />} />
                              {analytics.volume_avg_20 != null && <ReferenceLine y={analytics.volume_avg_20} stroke={colors.gray[400]} strokeDasharray="3 3" strokeWidth={1} />}
                              <Bar dataKey="volume" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                                {chartData.map((item, index) => {
                                  const isActionable = item.markerY != null;
                                  const color = isActionable ? getDisplayTone(item.display?.grade?.tone).color : colors.gray[300];
                                  return <Cell key={index} fill={color} fillOpacity={isActionable ? 0.7 : 0.5} />;
                                })}
                              </Bar>
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1, pt: 0.5, pb: 0.5 }}>
                          {[{ c: colors.success.main, label: 'A' }, { c: colors.info.main, label: 'B' }, { c: colors.warning.main, label: 'C' }, { c: colors.gray[400], label: 'D' }].map((gradeItem) => (
                            <Box key={gradeItem.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: gradeItem.c }} />
                              <Typography sx={{ fontSize: '0.6rem', color: colors.gray[500], fontWeight: 700 }}>{gradeItem.label}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                  <TableContainer data-html2canvas-ignore="true" sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...thSx, py: 0.6, px: 1.5 }}>{t('vsa.detail.headers.date')}</TableCell>
                          <TableCell sx={{ ...thSx, py: 0.6, px: 1, textAlign: 'center' }}>{t('vsa.detail.headers.grade')}</TableCell>
                          <TableCell sx={{ ...thSx, py: 0.6, px: 1, textAlign: 'center' }}>{t('vsa.detail.headers.score')}</TableCell>
                          <TableCell sx={{ ...thSx, py: 0.6, px: 1, textAlign: 'right' }}>{t('vsa.detail.headers.close')}</TableCell>
                          <TableCell sx={{ ...thSx, py: 0.6, px: 1, textAlign: 'right' }}>{t('vsa.detail.headers.entry')}</TableCell>
                          <TableCell sx={{ ...thSx, py: 0.6, px: 1.5, textAlign: 'right' }}>{t('vsa.detail.headers.stop')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((item, index) => (
                          <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: colors.gray[50] } }}>
                            <TableCell sx={{ ...tdSx, py: 0.5, px: 1.5 }}><Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: colors.gray[500] }}>{item.date?.slice(5)}</Typography></TableCell>
                            <TableCell sx={{ ...tdSx, py: 0.5, px: 1, textAlign: 'center' }}><Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: getDisplayTone(item.display?.grade?.tone).color }}>{item.display?.grade?.text || item.execution_grade}</Typography></TableCell>
                            <TableCell sx={{ ...tdSx, py: 0.5, px: 1, textAlign: 'center' }}><Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: getDisplayTone(item.display?.priority?.tone).color || scoreColor(item.priority_score), fontFamily: 'monospace' }}>{item.priority_score != null ? Math.round(item.priority_score) : '—'}</Typography></TableCell>
                            <TableCell sx={{ ...tdSx, py: 0.5, px: 1, textAlign: 'right' }}><Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}>{fmt(item.close)}</Typography></TableCell>
                            <TableCell sx={{ ...tdSx, py: 0.5, px: 1, textAlign: 'right' }}><Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: colors.info.main }}>{fmt(item.entry_mid)}</Typography></TableCell>
                            <TableCell sx={{ ...tdSx, py: 0.5, px: 1.5, textAlign: 'right' }}><Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: colors.error.main }}>{fmt(item.stop_loss)}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}